<script>
import LazyImage from "@/components/atoms/lazy-image";
import FormSelect from "@/components/molecules/form-select/index";
import Button from "@/components/atoms/button";
import Divider from "@/components/atoms/divider";
import Form from "@/components/molecules/form";
import FormRadio from "@/components/molecules/form-radio";
import FormInput from "@/components/molecules/form-input";
import FormTip from "@/components/molecules/form-tip";
import FormGroup from "@/components/molecules/form-group";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";
import Container from "@/components/atoms/container";
import StickyFooter from "@/components/molecules/sticky-footer";
import {
  redevanceFormEmits,
  redevanceFormProps,
  useRedevanceForm
} from "./mixin";
import { required } from "@vuelidate/validators";
import { isDate } from "@/utils/form-validators";

export default {
  name: "RedevanceForm",
  components: {
    LazyImage,
    FormSelect,
    Button,
    Divider,
    Form,
    FormRadio,
    FormInput,
    FormTip,
    FormGroup,
    StickyFooter,
    Container
  },
  props: { ...formValidationProps(), ...redevanceFormProps() },
  emits: [...redevanceFormEmits()],
  setup(props, context) {
    const { getState, getErrorMessage } = useFormValidation(props);
    const { model, usage, usageOptions, v$, applyRedevance, mounted } =
      useRedevanceForm(props, context);
    onMounted(mounted);
    return {
      model,
      usage,
      usageOptions,
      v$,
      applyRedevance,
      getState,
      getErrorMessage
    };
  },
  validations() {
    return {
      model: {
        nom: {
          required
        },
        prenom: {
          required
        },
        civilite: {
          required
        },
        date_naissance: {
          required,
          isDate
        },
        departement_naissance: {
          required
        },
        ville_naissance: {
          required
        }
      }
    };
  }
};
</script>

<template>
  <Form
    class="c-redevance-form-mobile"
    :error="error"
    @submit.prevent="applyRedevance"
  >
    <Container>
      <div class="u-mb-xlg">
        <h1 class="c-redevance-form-mobile__title u-mt-md">
          Redevance audiovisuelle
        </h1>
        <p class="c-redevance-form-mobile__intro">
          Votre commande comporte au moins un appareil soumis à cette redevance.
          Merci de bien vouloir compléter le formulaire.
          à destination de l’administration fiscale.
        </p>
        <Divider dotted />
        <div
          v-for="produit in usage"
          :key="produit.id"
        >
          <div class="c-redevance-form-mobile__product">
            <LazyImage
              :src="produit.visuel"
              dimension="48"
              :alt="produit.designation"
              class="c-redevance-form-mobile__product-img"
              width="48"
              height="48"
            />
            <p class="c-redevance-form-mobile__product-name">
              {{ produit.designation }}
            </p>
            <FormGroup>
              <FormSelect
                v-model="produit.usage_type"
                title="Usage"
                class="c-redevance-form-mobile__product-usage"
                default-value="Usage du produit *"
                :options="usageOptions"
              />
            </FormGroup>
          </div>
          <Divider dotted />
        </div>

        <h2 class="c-redevance-form-mobile__title u-mt-md">
          Informations concernant le futur propriétaire
        </h2>
        <div class="c-redevance-form-mobile__row">
          <label class="u-block u-font-13 u-mb-xs u-color-text-light">Civilité *</label>
          <FormGroup
            inline
            class="u-mb-sm"
            :state="getState(v$,'civilite', 'model')"
          >
            <FormRadio
              v-model="model.civilite"
              name="civilite"
              value="Mme"
              class="u-mr-lg"
            >
              Madame
            </FormRadio>
            <FormRadio
              v-model="model.civilite"
              name="civilite"
              value="M."
            >
              Monsieur
            </FormRadio>
            <template #message>
              <p>
                {{ getErrorMessage("civilite","Merci de renseigner votre civilité") }}
              </p>
            </template>
          </FormGroup>
        </div>
        <div class="c-redevance-form-mobile__row">
          <FormGroup
            :state="getState(v$,'nom', 'model')"
            class="c-redevance-form-mobile__form-group"
          >
            <FormInput
              v-model.trim="model.nom"
              :state="getState(v$,'nom', 'model')"
              size="lg"
            >
              Nom *
            </FormInput>
            <template #message>
              <p>
                {{ getErrorMessage("nom","Merci de renseigner votre nom") }}
              </p>
            </template>
          </FormGroup>
        </div>
        <div class="c-redevance-form-mobile__row">
          <FormGroup
            :state="getState(v$,'prenom', 'model')"
            class="c-redevance-form-mobile__form-group"
          >
            <FormInput
              v-model.trim="model.prenom"
              :state="getState(v$,'prenom', 'model')"
              size="lg"
            >
              Prénom *
            </FormInput>
            <template #message>
              <p>
                {{ getErrorMessage("prenom","Merci de renseigner votre prénom") }}
              </p>
            </template>
          </FormGroup>
        </div>

        <FormTip class="c-redevance-form-mobile__row">
          <FormGroup
            :state="getState(v$,'date_naissance', 'model')"
            class="c-redevance-form-mobile__form-group"
          >
            <FormInput
              v-model.trim="model.date_naissance"
              :state="getState(v$,'date_naissance', 'model')"
              size="lg"
              @blur="v$.model.date_naissance.$touch()"
              @input="v$.model.date_naissance.$reset()"
            >
              Date de naissance *
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.model.date_naissance.required.$invalid">
                  Merci de renseigner votre date de naissance
                </template>
                <template v-else>
                  {{ getErrorMessage("date_naissance","Cette date de naissance est invalide") }}
                </template>
              </p>
            </template>
          </FormGroup>
          <template #tip>
            <div>
              Exemple 23/09/1987
            </div>
          </template>
        </FormTip>

        <FormTip class="c-redevance-form-mobile__row">
          <FormGroup
            :state="getState(v$,'departement_naissance', 'model')"
            class="c-redevance-form-mobile__form-group"
          >
            <FormInput
              v-model.trim="model.departement_naissance"
              :state="getState(v$,'departement_naissance', 'model')"
              size="lg"
            >
              Département de naissance *
            </FormInput>
            <template #message>
              <p>
                {{ getErrorMessage("departement_naissance","Merci de renseigner votre département de naissance") }}
              </p>
            </template>
          </FormGroup>
          <template #tip>
            <div>
              Exemple 75
            </div>
          </template>
        </FormTip>

        <FormTip class="c-redevance-form-mobile__row">
          <FormGroup
            :state="getState(v$,'ville_naissance', 'model')"
            class="c-redevance-form-mobile__form-group"
          >
            <FormInput
              v-model.trim="model.ville_naissance"
              :state="getState(v$,'ville_naissance', 'model')"
              size="lg"
            >
              Ville de naissance *
            </FormInput>
            <template #message>
              <p>
                {{ getErrorMessage("ville_naissance","Merci de renseigner votre ville de naissance") }}
              </p>
            </template>
          </FormGroup>
          <template #tip>
            <div>
              Exemple Paris
            </div>
          </template>
        </FormTip>
      </div>
    </Container>
    <StickyFooter
      class="u-flex u-flex-column"
      :in-dom="false"
    >
      <Button
        radius="full"
        variant="secondary"
        size="md"
        type="submit"
      >
        Je valide mes informations
      </Button>
    </StickyFooter>
  </Form>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/redevance-form-mobile.scss";
</style>
